import React from "react";

// Defensive: LLMs occasionally return objects where the UI expects strings.
const toStr = (x) => {
  if (x == null) return "";
  if (typeof x === "string") return x;
  if (typeof x === "number" || typeof x === "boolean") return String(x);
  if (Array.isArray(x)) return x.map(toStr).filter(Boolean).join(" · ");
  if (typeof x === "object") {
    if (x.field && x.type) return `${x.field} (${x.type})`;
    if (x.name && x.detail) return `${x.name} — ${x.detail}`;
    if (x.label && x.value) return `${x.label}: ${x.value}`;
    return Object.entries(x).map(([k, v]) => `${k}: ${toStr(v)}`).join(" · ");
  }
  return String(x);
};

export const Tag = ({ children, intent = "default" }) => {
  const styles = {
    default: "bg-transparent text-[#0A0A0A]/70 border-[#0A0A0A]/15",
    klein: "bg-[#002FA7] text-white border-[#002FA7]",
    high: "bg-[#0A0A0A] text-[#F9F8F6] border-[#0A0A0A]",
    medium: "bg-[#EFEFE8] text-[#0A0A0A] border-[#0A0A0A]/15",
    low: "bg-transparent text-[#5C5C5C] border-[#0A0A0A]/12",
  };
  return (
    <span
      className={`font-mono text-[10px] tracking-[0.18em] uppercase px-2 py-1 border ${styles[intent] || styles.default}`}
    >
      {children}
    </span>
  );
};

export const SectionHeader = ({ number, eyebrow, title, kicker }) => (
  <div className="mb-10 border-b border-[#0A0A0A]/15 pb-6">
    <div className="flex items-baseline gap-4 mb-3">
      <span className="font-mono text-xs tracking-[0.22em] uppercase text-[#002FA7]">{toStr(number)}</span>
      <span className="font-mono text-xs tracking-[0.22em] uppercase text-[#5C5C5C]">{toStr(eyebrow)}</span>
    </div>
    <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#0A0A0A]">
      {toStr(title)}
    </h2>
    {kicker ? (
      <p className="mt-4 font-sans text-base text-[#0A0A0A]/70 max-w-3xl leading-relaxed">{toStr(kicker)}</p>
    ) : null}
  </div>
);

export const Cell = ({ label, value, note, accent = false, testid }) => (
  <div
    data-testid={testid}
    className={`border border-[#0A0A0A]/15 p-6 sm:p-7 ${accent ? "bg-[#0A0A0A] text-[#F9F8F6]" : "bg-[#F9F8F6]"}`}
  >
    <div
      className={`font-mono text-[10px] tracking-[0.22em] uppercase ${accent ? "text-[#F9F8F6]/60" : "text-[#5C5C5C]"} mb-3`}
    >
      {toStr(label)}
    </div>
    <div className="font-serif text-3xl sm:text-4xl font-light tracking-tight leading-none mb-3">
      {toStr(value)}
    </div>
    {note ? (
      <div className={`text-xs leading-relaxed ${accent ? "text-[#F9F8F6]/70" : "text-[#0A0A0A]/65"}`}>
        {toStr(note)}
      </div>
    ) : null}
  </div>
);

export const ScoreBar = ({ score, max = 10 }) => {
  const pct = Math.max(0, Math.min(100, (score / max) * 100));
  return (
    <div className="flex items-center gap-3">
      <div className="font-mono text-xs tracking-widest text-[#002FA7] w-10 text-right">
        {score}/{max}
      </div>
      <div className="flex-1 h-1 bg-[#0A0A0A]/10">
        <div className="h-1 bg-[#002FA7]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};
